import React, { useState, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useBackend } from "shared/hooks/backend-hook";
import { AuthContext } from "shared/context/auth-context";
import GenericModal from "components/shared/GenericModal";
import { PLANS } from "@routebot/constants";

/**
 * Custom hook for checking usage allowance before operations
 * Provides a modal component and check functions for different usage types
 */
export const useUsageGuard = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const { checkUsageAllowance } = useBackend();
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const isDemo = auth?.company?.isDemo;
  const isUnlimited = auth?.company?.plan === PLANS.UNLIMITED;

  /**
   * Shows demo account limit modal
   */
  const showDemoLimitModal = useCallback(() => {
    setModalTitle(t("usageLimitExceededForDemo"));
    setModalMessage(t("usageLimitExceededForDemoDetailed"));
    setShowModal(true);
  }, [t]);

  /**
   * Check if user has enough quota for MAPS__DIRECTIONS_SINGLE operations
   * Shows a modal with appropriate message if quota is exceeded
   * For demo accounts, shows a special demo limit message
   * @param {number} requiredUnits - Number of units required for the operation
   * @returns {Promise<{allowed: boolean, remaining: number, requested: number}>}
   */
  const checkUsageForMapsDirectionsSingle = useCallback(
    async (requiredUnits) => {
      if (isUnlimited) {
        return {
          allowed: true,
          remaining: Infinity,
          requested: requiredUnits,
        };
      }
      const usageCheck = await checkUsageAllowance("MAPS__DIRECTIONS_SINGLE", requiredUnits);

      if (!usageCheck.allowed) {
        if (isDemo) {
          showDemoLimitModal();
        } else {
          setModalTitle(t("usageLimitExceededForMapsDirectionsSingle"));
          setModalMessage(
            t("usageLimitExceededForMapsDirectionsSingleDetailed", {
              remaining: usageCheck.remaining,
              requested: requiredUnits,
            })
          );
          setShowModal(true);
        }
      }

      return {
        allowed: usageCheck.allowed,
        remaining: usageCheck.remaining,
        requested: requiredUnits,
      };
    },
    [checkUsageAllowance, t, isDemo, showDemoLimitModal]
  );

  /**
   * Check if user has enough quota for MAPS__ADDRESS_TO_NEAREST_ROAD_LOCATION operations
   * Shows a modal with appropriate message if quota is exceeded
   * For demo accounts, shows a special demo limit message
   * @param {number} requiredUnits - Number of units required for the operation (default: 1)
   * @returns {Promise<{allowed: boolean, remaining: number, requested: number}>}
   */
  const checkUsageForMapsAddressToLocation = useCallback(
    async (requiredUnits = 1) => {
      if (isUnlimited) {
        return {
          allowed: true,
          remaining: Infinity,
          requested: requiredUnits,
        };
      }
      const usageCheck = await checkUsageAllowance("MAPS__ADDRESS_TO_NEAREST_ROAD_LOCATION", requiredUnits);

      if (!usageCheck.allowed) {
        if (isDemo) {
          showDemoLimitModal();
        } else {
          setModalTitle(t("usageLimitExceededForMapsAddressToLocation"));
          setModalMessage(t("usageLimitExceededForMapsAddressToLocationDetailed"));
          setShowModal(true);
        }
      }

      return {
        allowed: usageCheck.allowed,
        remaining: usageCheck.remaining,
        requested: requiredUnits,
      };
    },
    [checkUsageAllowance, t, isDemo, showDemoLimitModal]
  );

  /**
   * Check if user has enough quota for MAPS__LOCATION_TO_NEAREST_ROAD_ADDRESS operations
   * Shows a modal with appropriate message if quota is exceeded
   * For demo accounts, shows a special demo limit message
   * @param {number} requiredUnits - Number of units required for the operation (default: 1)
   * @returns {Promise<{allowed: boolean, remaining: number, requested: number}>}
   */
  const checkUsageForMapsLocationToAddress = useCallback(
    async (requiredUnits = 1) => {
      if (isUnlimited) {
        return {
          allowed: true,
          remaining: Infinity,
          requested: requiredUnits,
        };
      }
      const usageCheck = await checkUsageAllowance("MAPS__LOCATION_TO_NEAREST_ROAD_ADDRESS", requiredUnits);

      if (!usageCheck.allowed) {
        if (isDemo) {
          showDemoLimitModal();
        } else {
          setModalTitle(t("usageLimitExceededForMapsLocationToAddress"));
          setModalMessage(t("usageLimitExceededForMapsLocationToAddressDetailed"));
          setShowModal(true);
        }
      }

      return {
        allowed: usageCheck.allowed,
        remaining: usageCheck.remaining,
        requested: requiredUnits,
      };
    },
    [checkUsageAllowance, t, isDemo, showDemoLimitModal]
  );

  /**
   * Generic usage check with custom error messages
   * For demo accounts, shows a special demo limit message
   * @param {string} usageType - The usage type to check (e.g., "MAPS__DIRECTIONS_SINGLE")
   * @param {number} requiredUnits - Number of units required
   * @param {string} titleKey - Translation key for modal title
   * @param {string} messageKey - Translation key for modal message
   * @returns {Promise<{allowed: boolean, remaining: number, requested: number}>}
   */
  const checkUsage = useCallback(
    async (usageType, requiredUnits, titleKey = "usageLimitExceeded", messageKey = "usageLimitExceededDetailed") => {
      if (isUnlimited) {
        return {
          allowed: true,
          remaining: Infinity,
          requested: requiredUnits,
        };
      }
      const usageCheck = await checkUsageAllowance(usageType, requiredUnits);

      if (!usageCheck.allowed) {
        if (isDemo) {
          showDemoLimitModal();
        } else {
          setModalTitle(t(titleKey));
          setModalMessage(
            t(messageKey, {
              remaining: usageCheck.remaining,
              requested: requiredUnits,
            })
          );
          setShowModal(true);
        }
      }

      return {
        allowed: usageCheck.allowed,
        remaining: usageCheck.remaining,
        requested: requiredUnits,
      };
    },
    [checkUsageAllowance, t, isDemo, showDemoLimitModal]
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  /**
   * Modal component to be rendered in the parent component
   */
  const UsageGuardModal = useCallback(
    () => <GenericModal message={modalMessage} title={modalTitle} onClose={closeModal} showModal={showModal} />,
    [modalMessage, modalTitle, showModal, closeModal]
  );

  return {
    checkUsageForMapsDirectionsSingle,
    checkUsageForMapsAddressToLocation,
    checkUsageForMapsLocationToAddress,
    checkUsage,
    UsageGuardModal,
    closeModal,
    isModalOpen: showModal,
  };
};

export default useUsageGuard;
